import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import {
  CreateRakutenAdvertiserDto,
  RakutenAdvertiserResponse,
  RakutenAdvertisersListResponse,
  RakutenAdvertisersQuery,
  RakutenPartnershipsListResponse
} from '../../models/rakuten-advertiser';
import { RakutenFilterPayload, RakutenReportResponse, RakutenSubpublisher } from '../../models/rakuten';
import { HttpService } from '../../../services/http/http.service';

export interface RakutenProductsQuery {
  mid: string;
  keyword: string;
  max?: number;
  pagenumber?: number;
  sort?: string;
  sorttype?: 'asc' | 'desc';
}

export interface RakutenProductItem {
  mid: string;
  merchantName: string;
  linkId: string;
  createdOn: string;
  sku: string;
  productName: string;
  category: {
    primary: string;
    secondary: string;
  };
  price: {
    currency: string;
    value: number | null;
  };
  salePrice: {
    currency: string;
    value: number | null;
  };
  upcCode: string;
  description: {
    short: string;
    long: string;
  };
  keywords: string;
  linkUrl: string;
  imageUrl: string;
}

export interface RakutenProductsResponse {
  totalMatches: number;
  totalPages: number;
  pageNumber: number;
  items: RakutenProductItem[];
}

/** GET /rakuten/affiliate/link/:briefId/:creatorId — persisted Linksynergy deeplink after hire. */
export interface RakutenAffiliateLinkApiDto {
  id?: string;
  briefId?: string;
  creatorId?: string;
  trackingLink?: string | null;
  advertiserId?: string | null;
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RakutenService {
  constructor(
    private httpService: HttpService
  ) {}

  /**
   * Loads static JSON from assets and applies filtering client-side.
   * Keeps the same method name so you can swap to real backend later.
   */
  getAdvertiserById(advertiserId: string): Observable<RakutenAdvertiserResponse> {
    return this.httpService.runHttpCall(
      'GET',
      `/rakuten/advertisers/${advertiserId}`,
      'application/json'
    ) as Observable<RakutenAdvertiserResponse>;
  }

  /** Create a record in rakuten_advertiser table. */
  createRakutenAdvertiser(dto: CreateRakutenAdvertiserDto): Observable<unknown> {
    return this.httpService.runHttpCall(
      'POST',
      '/rakuten/advertisers',
      'application/json',
      dto
    );
  }

  /** Create a single Rakuten publisher record. */
  createRakutenPublisher(payload: RakutenSubpublisher): Observable<unknown> {
    return this.httpService.runHttpCall(
      'POST',
      '/rakuten/publishers/subpublishers',
      'application/json',
      payload
    );
  }

  /** Bulk create Rakuten publisher records. */
  createRakutenPublishersBulk(items: RakutenSubpublisher[]): Observable<unknown> {
    return this.httpService.runHttpCall(
      'POST',
      '/rakuten/publishers/subpublishers/bulk',
      'application/json',
      items
    );
  }

  getAdvertisers(query?: RakutenAdvertisersQuery): Observable<RakutenAdvertisersListResponse> {
    const params = new URLSearchParams();
    if (query?.limit != null) params.set('limit', String(query.limit));
    if (query?.offset != null) params.set('offset', String(query.offset));
    if (query?.search != null && query.search !== '') params.set('search', query.search);
    if (query?.advertiserCategoryId != null) params.set('advertiserCategoryId', String(query.advertiserCategoryId));
    if (query?.countryCode != null && query.countryCode !== '') params.set('countryCode', query.countryCode);
    if (query?.status != null && query.status !== '') params.set('status', query.status);
    const qs = params.toString();
    const endpoint = qs ? `/rakuten/advertisers?${qs}` : '/rakuten/advertisers';
    return this.httpService.runHttpCall(
      'GET',
      endpoint,
      'application/json'
    ) as Observable<RakutenAdvertisersListResponse>;
  }

  getPartnerships(query?: {
    advertiser_status?: string;
    partner_status?: string;
    page?: number;
    limit?: number;
  }): Observable<RakutenPartnershipsListResponse> {
    const params = new URLSearchParams();
    if (query?.advertiser_status) params.set('advertiser_status', query.advertiser_status);
    if (query?.partner_status) params.set('partner_status', query.partner_status);
    if (query?.page != null) params.set('page', String(query.page));
    if (query?.limit != null) params.set('limit', String(query.limit));

    const qs = params.toString();
    const endpoint = qs ? `/rakuten/partnerships?${qs}` : '/rakuten/partnerships';

    return this.httpService.runHttpCall(
      'GET',
      endpoint,
      'application/json'
    ) as Observable<RakutenPartnershipsListResponse>;
  }

  getAffiliateLink(briefId: string, creatorId: string): Observable<RakutenAffiliateLinkApiDto | null> {
    return this.httpService.runHttpCall(
      'GET',
      `/rakuten/affiliate/link/${briefId}/${creatorId}`,
      'application/json',
    ) as Observable<RakutenAffiliateLinkApiDto | null>;
  }

  getProducts(query: RakutenProductsQuery): Observable<RakutenProductsResponse> {
    const params = new URLSearchParams();
    params.set('mid', query.mid);
    params.set('keyword', query.keyword);
    params.set('max', String(query.max ?? 20));
    params.set('pagenumber', String(query.pagenumber ?? 1));
    params.set('sort', query.sort ?? 'productname');
    params.set('sorttype', query.sorttype ?? 'asc');

    return this.httpService.runHttpCall(
      'GET',
      `/rakuten/products?${params.toString()}`,
      'application/json',
    ) as Observable<RakutenProductsResponse>;
  }

  filterRakutenReportData(payload: RakutenFilterPayload): Observable<RakutenReportResponse> {
    const params = new URLSearchParams();
    params.set('date_range', 'this-month');
    if (typeof payload.minClicks === 'number') params.set('minClicks', String(payload.minClicks));
    if (typeof payload.minOrders === 'number') params.set('minOrders', String(payload.minOrders));
    if (payload.searchTerm) params.set('searchTerm', payload.searchTerm);
    if (payload.page != null) params.set('page', String(payload.page));
    if (payload.take != null) params.set('take', String(payload.take));
    if (payload.sort?.field) params.set('sortField', payload.sort.field);
    if (payload.sort?.dir) params.set('sortDir', payload.sort.dir);

    const qs = params.toString();
    return (
      this.httpService.runHttpCall(
        'GET',
        `/rakuten/reports/affiliate-summary${qs ? `?${qs}` : ''}`,
        'application/json',
      ) as Observable<RakutenReportResponse>
    ).pipe(
      catchError((error) => {
        console.error('Error fetching Rakuten affiliate summary:', error);
        return throwError(
          () => new Error('Failed to fetch Rakuten Reports. Please try again.'),
        );
      }),
    );
  }
}
